/**
 * Application constants for Patchwerk
 */

// Version
export const APP_VERSION = "0.10.1";
export const BUILD_TIMESTAMP = "2026-03-06T16:07:12.256Z";

// Storage keys
export const STORAGE_KEY = 'patchwerk-patch';
export const VIEWPORT_STORAGE_KEY = 'patchwerk-viewport';
export const FAVORITES_STORAGE_KEY = 'patchwerk-favorites';
export const SAVED_PATCHES_STORAGE_KEY = 'patchwerk_saved_patches';
export const CURRENT_PATCH_STORAGE_KEY = 'patchwerk-current-patch-name';
export const DEBUG_SETTINGS_STORAGE_KEY = 'patchwerk-debug-settings';
export const RECENT_PATCHES_STORAGE_KEY = 'patchwerk-recent-patches';
export const VERSIONS_STORAGE_KEY = 'patchwerk-versions';
export const MIGRATION_PROMPTED_KEY = 'patchwerk-migration-prompted';

// Limits
export const HISTORY_MAX_SIZE = 50;
export const RECENT_PATCHES_MAX = 5;
export const AUTOSAVE_DEBOUNCE_MS = 2000;
export const MAX_VERSIONS = 50;

// Timing
export const LONG_PRESS_DURATION = 500;
export const DOUBLE_TAP_DELAY = 300;
export const DOUBLE_TAP_DISTANCE = 20;
export const SINGLE_TAP_TIMEOUT = 300;
export const SINGLE_TAP_MOVEMENT = 10;
export const MULTI_FINGER_TAP_TIMEOUT = 200;
export const MULTI_FINGER_TAP_MOVEMENT = 15;

// Clipboard prefixes
export const PATCHWERK_CLIPBOARD_PREFIX = 'patchwerk_device';
export const PATCHWERK_SELECTION_PREFIX = 'patchwerk_selection';

// Shareable URL prefixes (clipboard patches become clickable URLs)
export const PATCHWERK_URL_PREFIX_PROD = 'https://patchwerk.fly.dev/patch=';
export const PATCHWERK_URL_PREFIX_DEV = 'https://patchwerk-dev.fly.dev/patch=';

// Port type display names
export const PORT_TYPE_DISPLAY_NAMES = {
    'audio': 'Audio',
    'midi': 'MIDI',
    'midi-rtp': 'MIDI RTP',
    'midi-usb-host': 'MIDI USB Host',
    'midi-usb-device': 'MIDI USB Device',
    'cv': 'CV',
    'gate': 'Gate',
    'clock': 'Clock',
    'media': 'Media',
    'insert': 'Insert',
    'aes-ebu': 'AES/EBU',
    'spdif': 'S/PDIF',
    'adat': 'ADAT',
    'madi': 'MADI',
    'dante': 'Dante',
    'aes67': 'AES67',
    'word-clock': 'Word Clock',
    'dmx': 'DMX'
};

// Multi-type presets for flexible ports
export const MULTI_TYPE_PRESETS = {
    'eurorack': {
        label: 'Eurorack',
        types: ['audio', 'cv', 'gate', 'clock'],
        description: 'Analog modular (accepts any analog signal)'
    },
    'line': {
        label: 'Line/Instrument',
        types: ['audio'],
        description: 'Standard audio only'
    },
    'control': {
        label: 'Control',
        types: ['cv', 'gate', 'clock'],
        description: 'Modular control signals'
    }
};

// Physical connector types
export const CONNECTOR_TYPES = {
    // XLR variants
    'xlr-m': { label: 'XLR Male', icon: '◯', shortCode: 'XLR-M' },
    'xlr-f': { label: 'XLR Female', icon: '◉', shortCode: 'XLR-F' },
    // 1/4" variants
    'trs': { label: '1/4" TRS', icon: '◎', shortCode: 'TRS' },
    'ts': { label: '1/4" TS', icon: '○', shortCode: 'TS' },
    // 3.5mm / 1/8"
    '3.5mm': { label: '3.5mm (1/8")', icon: '·', shortCode: '3.5' },
    // MIDI
    'midi-din': { label: 'MIDI DIN (5-pin)', icon: '◐', shortCode: 'DIN' },
    // USB variants
    'usb-a': { label: 'USB-A', icon: '▯', shortCode: 'USB-A' },
    'usb-b': { label: 'USB-B', icon: '▢', shortCode: 'USB-B' },
    'usb-c': { label: 'USB-C', icon: '▬', shortCode: 'USB-C' },
    'usb-mini': { label: 'USB Mini', icon: '▫', shortCode: 'Mini' },
    'usb-micro': { label: 'USB Micro', icon: '▪', shortCode: 'Micro' },
    // Network
    'rj45': { label: 'RJ45', icon: '▦', shortCode: 'RJ45' },
    // D-sub variants
    'db25': { label: 'D-sub (DB25)', icon: '▤', shortCode: 'DB25', subgroupSize: 8 },
    'd-sub': { label: 'D-sub (DB25)', icon: '▤', shortCode: 'DB25', subgroupSize: 8 },
    'db9': { label: 'D-sub (DB9)', icon: '▨', shortCode: 'DB9', subgroupSize: 4 },
    // Pro audio
    'speakon': { label: 'Speakon', icon: '⬡', shortCode: 'SPK' },
    'euroblock': { label: 'Euroblock/Phoenix', icon: '▥', shortCode: 'EUR' },
    // Consumer audio
    'rca': { label: 'RCA', icon: '●', shortCode: 'RCA' },
    // Video/broadcast
    'bnc': { label: 'BNC', icon: '◈', shortCode: 'BNC' },
    // Combo
    'combo': { label: 'Combo (XLR + 1/4")', icon: '⊕', shortCode: 'CMB' },
    // Optical
    'toslink': { label: 'TOSLINK (Optical)', icon: '◇', shortCode: 'OPT' },
    // XLR variants
    'xlr-3': { label: 'XLR 3-pin', icon: '◯', shortCode: 'XLR3' },
    'xlr-5': { label: 'XLR 5-pin', icon: '◯', shortCode: 'XLR5' },
    // Network
    'ethercon': { label: 'EtherCon', icon: '▦', shortCode: 'ECON' },
    // Fiber
    'fiber-sc': { label: 'SC Fiber', icon: '◈', shortCode: 'SC' },
    'fiber-lc': { label: 'LC Fiber', icon: '◈', shortCode: 'LC' }
};

// Connector compatibility matrix
// Key: connector type, Value: array of compatible connector types
// Same type is always compatible (implicit in canConnectConnectors)
export const CONNECTOR_COMPATIBILITY = {
    'xlr-m': ['xlr-f', 'xlr-3', 'combo', 'trs', 'ts'],
    'xlr-f': ['xlr-m', 'xlr-3', 'trs', 'ts'],
    'trs': ['ts', 'combo', 'xlr-m', 'xlr-f', '3.5mm', 'rca'],
    'ts': ['trs', 'combo', 'xlr-m', 'xlr-f', '3.5mm', 'rca'],
    '3.5mm': ['trs', 'ts', 'midi-din'],
    'midi-din': ['3.5mm'],
    'usb-a': ['usb-b', 'usb-c', 'usb-mini', 'usb-micro'],
    'usb-b': ['usb-a'],
    'usb-c': ['usb-a', 'usb-c'],
    'usb-mini': ['usb-a'],
    'usb-micro': ['usb-a'],
    'combo': ['xlr-m', 'trs', 'ts'],
    'rca': ['ts', 'trs'],
    'euroblock': ['trs', 'ts', 'xlr-m', 'xlr-f'],
    // XLR variants - cross-compatible
    'xlr-3': ['xlr-m', 'xlr-f'],
    'xlr-5': [],  // 5-pin is only compatible with itself
    // Network - EtherCon is rugged RJ45
    'rj45': ['ethercon'],
    'ethercon': ['rj45']
    // No entries = only matches itself: db25, db9, speakon, bnc, toslink, fiber-sc, fiber-lc
};
